import PropTypes from "prop-types";
import { t } from "i18next";
import { formatRecommendationAction } from "../../utils/formatRecommendationAction";

const AssessmentProgressBanner = ({
  summary,
  onStartAssessment,
  onCompleteDecision,
  onCreateAssessment,
}) => {
  if (!summary) return null;

  const status = summary.status?.toLowerCase();
  const decisionType = summary.decisionType;
  const recommendation = summary.recommendation;
  const studentLevels = summary.studentLevels;

  if (decisionType === "conclusive") return null;

  const isSubmitted = status === "submitted";
  const isStarted = status === "started" || status === "created";
  const isEvaluated = status === "evaluated";
  const isDone = status === "done";
  const hasRecommendation = Boolean(recommendation?.level);

  if (
    !isSubmitted &&
    !isStarted &&
    !isEvaluated &&
    !(isDone && hasRecommendation)
  ) {
    return null;
  }

  const isBlue = isDone && hasRecommendation;
  const themeClass = isBlue
    ? "assessment-banner-blue"
    : "assessment-banner-orange";

  const expectedLevelCode = studentLevels?.expected?.code;

  const recommendationActionText = formatRecommendationAction(recommendation);

  const getSubtitle = () => {
    if (isStarted) {
      return t("profile.studentAnswering");
    }
    if (isSubmitted) {
      return t("profile.awaitingAssessmentSubtitle");
    }
    if (isEvaluated) {
      return t("profile.completeDecisionSubtitle");
    }
    if (isDone && hasRecommendation) {
      return t("profile.createNewAssessmentSubtitle");
    }
    return "";
  };

  return (
    <div
      className={`assessment-progress-banner ${themeClass} d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4`}
    >
      <div className="banner-info-side d-flex align-items-center gap-3">
        <div className="banner-status-circle d-flex align-items-center justify-content-center">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div className="banner-text-block text-end">
          <h3 className="banner-title mb-1">
            {t("profile.assessmentInProgress")}
          </h3>
          <p className="banner-subtitle mb-0">{getSubtitle()}</p>
        </div>
      </div>

      {hasRecommendation && (
        <div className="banner-badges-side d-flex align-items-center flex-wrap gap-2">
          {recommendationActionText && (
            <div className="banner-pill banner-pill-action d-flex align-items-center gap-2">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L7 17l-5-5" />
                <path d="M22 10l-7.5 7.5-2-2" />
              </svg>
              <span>
                <strong>{t("profile.suggestedAction")}:</strong>{" "}
                {recommendationActionText}
              </span>
            </div>
          )}

          {expectedLevelCode && (
            <div className="banner-pill banner-pill-expected d-flex align-items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>
                {t("profile.expectedLevel")}:{" "}
                <strong>{expectedLevelCode}</strong>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="banner-button-side">
        {isSubmitted && onStartAssessment && (
          <button
            type="button"
            className="btn btn-banner-action btn-banner-orange d-flex align-items-center gap-2"
            onClick={onStartAssessment}
          >
            <span>{t("profile.startGrading")}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {isEvaluated && onCompleteDecision && (
          <button
            type="button"
            className="btn btn-banner-action btn-banner-orange d-flex align-items-center gap-2"
            onClick={onCompleteDecision}
          >
            <span>{t("profile.completeDecision")}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {isBlue && onCreateAssessment && (
          <button
            type="button"
            className="btn btn-banner-action btn-banner-blue d-flex align-items-center gap-2"
            onClick={onCreateAssessment}
          >
            <span>{t("profile.createNewAssessment")}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

AssessmentProgressBanner.propTypes = {
  summary: PropTypes.shape({
    status: PropTypes.string,
    decisionType: PropTypes.string,
    studentLevels: PropTypes.object,
    recommendation: PropTypes.object,
  }),
  onStartAssessment: PropTypes.func,
  onCompleteDecision: PropTypes.func,
  onCreateAssessment: PropTypes.func,
};

export default AssessmentProgressBanner;
